import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { 
  Settings, 
  User, 
  Palette, 
  Download, 
  Monitor, 
  Save, 
  LogOut,
  Bell,
  Cloud,
  HardDrive,
  Mail,
  Eye,
  Sparkles,
  X,
  Type
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const SettingsPanel = ({ user, onClose, onLogout, onUserUpdate }) => {
  const [settings, setSettings] = useState({
    // Caption settings
    default_caption_style: 'white',
    default_caption_position: 'bottom',
    
    // Export settings
    default_export_resolution: '1080p',
    default_export_format: 'mp4',
    
    // UI settings
    timeline_view: 'horizontal',
    theme: 'dark',
    auto_save_interval: 10,
    
    // Notification settings
    email_notifications: true,
    export_complete_notifications: true
  });
  const [loading, setLoading] = useState(false);
  const [storageUsage, setStorageUsage] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    if (user && user.settings) {
      setSettings(user.settings);
    }
    if (user && user.storage_usage) {
      setStorageUsage(user.storage_usage);
    }
  }, [user]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          settings: settings
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        onUserUpdate(updatedUser);
        toast({
          title: "Settings saved!",
          description: "Your preferences have been updated successfully.",
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: "Failed to save settings. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStoragePercentage = () => {
    if (!storageUsage) return 0;
    return (storageUsage.used_mb / storageUsage.limit_mb) * 100;
  };

  const formatStorage = (mb) => {
    if (mb < 1024) return `${mb.toFixed(1)}MB`;
    return `${(mb / 1024).toFixed(1)}GB`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl h-[90vh] bg-gray-900 border-gray-700 flex flex-col">
        <CardHeader className="border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-600 rounded-lg">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Settings</CardTitle>
                <p className="text-gray-400 text-sm">Customize your FnctionOS experience</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* User Profile Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Profile</h3>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-medium">{user?.full_name}</h4>
                  <p className="text-gray-400 text-sm">{user?.email}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge className="bg-blue-600 text-white text-xs">
                      {user?.role || 'User'}
                    </Badge>
                    {user?.is_verified && (
                      <Badge className="bg-green-600 text-white text-xs">
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Storage Usage */}
          {storageUsage && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <HardDrive className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">Storage Usage</h3>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-300 text-sm">
                    {formatStorage(storageUsage.used_mb)} of {formatStorage(storageUsage.limit_mb)} used
                  </span>
                  <span className="text-gray-400 text-sm">
                    {getStoragePercentage().toFixed(1)}%
                  </span>
                </div>
                <Progress 
                  value={getStoragePercentage()} 
                  className="h-2 mb-3"
                />
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div>
                    <p className="text-gray-400">Projects</p>
                    <p className="text-white font-medium">{storageUsage.project_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Total Edits</p>
                    <p className="text-white font-medium">{user?.total_edits || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Exports</p>
                    <p className="text-white font-medium">{user?.total_exports || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Separator className="bg-gray-700" />

          {/* Caption Settings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Type className="w-5 h-5 text-yellow-400" />
              <h3 className="text-lg font-semibold text-white">Caption Defaults</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Style</label>
                <select
                  value={settings.default_caption_style}
                  onChange={(e) => handleSettingChange('default_caption_style', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="white">White</option>
                  <option value="yellow">Yellow</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Position</label>
                <select
                  value={settings.default_caption_position}
                  onChange={(e) => handleSettingChange('default_caption_position', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="bottom">Bottom</option>
                  <option value="center">Center</option>
                  <option value="top">Top</option>
                </select>
              </div>
            </div>
          </div>

          {/* Export Settings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Download className="w-5 h-5 text-green-400" />
              <h3 className="text-lg font-semibold text-white">Export Defaults</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Resolution</label>
                <select
                  value={settings.default_export_resolution}
                  onChange={(e) => handleSettingChange('default_export_resolution', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="720p">720p (HD)</option>
                  <option value="1080p">1080p (Full HD)</option>
                  <option value="4k">4K (Ultra HD)</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Format</label>
                <select
                  value={settings.default_export_format}
                  onChange={(e) => handleSettingChange('default_export_format', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="mp4">MP4</option>
                  <option value="mov">MOV</option>
                </select>
              </div>
            </div>
          </div>

          {/* UI Settings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Monitor className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Interface</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Timeline View</label>
                <select
                  value={settings.timeline_view}
                  onChange={(e) => handleSettingChange('timeline_view', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Theme</label>
                <select
                  value={settings.theme}
                  onChange={(e) => handleSettingChange('theme', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Auto-save (seconds)</label>
                <select
                  value={settings.auto_save_interval}
                  onChange={(e) => handleSettingChange('auto_save_interval', parseInt(e.target.value))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                >
                  <option value={5}>5 seconds</option>
                  <option value={10}>10 seconds</option>
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-orange-400" />
              <h3 className="text-lg font-semibold text-white">Notifications</h3>
            </div>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={(e) => handleSettingChange('email_notifications', e.target.checked)}
                  className="form-checkbox h-4 w-4 text-purple-600 bg-gray-700 border-gray-600 rounded"
                />
                <span className="text-gray-300">Email notifications</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.export_complete_notifications}
                  onChange={(e) => handleSettingChange('export_complete_notifications', e.target.checked)}
                  className="form-checkbox h-4 w-4 text-purple-600 bg-gray-700 border-gray-600 rounded"
                />
                <span className="text-gray-300">Export completion notifications</span>
              </label>
            </div>
          </div>
        </CardContent>

        {/* Footer */}
        <div className="border-t border-gray-700 p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={onLogout}
              className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="border-gray-600 text-gray-400 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={saveSettings}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SettingsPanel;